import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import Tooltip from 'ui/components/Tooltip/Tooltip'

import { formatElapsed, STEPS } from '../OptJobPhaseProgress.helper'
import { StepState } from '../OptJobPhaseProgress.types'
import { QueueItemRow } from './OptJobMultiPhaseProgress.helper'

type ProgressTone = 'light' | 'dark'

/** Stands in for a queue item that has no subtype of its own, alongside ones that do. */
const NO_SUBTYPE_LABEL = '-'

/**
 * Suggests a break after each underscore so long subtype names wrap, without altering
 * the rendered text. The parts come from splitting one string and are re-rendered
 * whole whenever it changes, so their position is a stable identity.
 */
function withWrapPoints(label: string): ReactNode {
  return label.split('_').map((part, index) => (
    <Fragment key={index}>
      {index > 0 && (
        <>
          _<wbr />
        </>
      )}
      {part}
    </Fragment>
  ))
}

type OptJobMultiPhaseProgressMatrixProps = {
  rows: QueueItemRow[]
  tone?: ProgressTone
}

const TONE_CLASSES = {
  light: {
    header: 'text-gray-500',
    label: 'font-medium text-gray-800',
    pendingCell: 'bg-gray-200',
  },
  dark: {
    header: 'text-gray-400',
    label: 'font-medium text-gray-100',
    pendingCell: 'bg-gray-600',
  },
} as const

function cellClassName(state: StepState, pendingClass: string): string {
  const base = 'h-5 w-full rounded-md flex items-center justify-center'
  switch (state) {
    case 'done':
      return `${base} bg-success-4`
    case 'active':
      return `${base} bg-info-3 animate-pulse`
    case 'failed':
      return `${base} bg-danger-3`
    case 'unsolvable':
      return `${base} bg-warning-5`
    default:
      return `${base} ${pendingClass}`
  }
}

/**
 * Presentational matrix: one row per queue item and one column per phase in the
 * shared `STEPS` model. Each phase cell is a pill showing its elapsed duration
 * when available, whether the item is still running or has reached its end state.
 * The label and per-item total columns are only worth their width when there is
 * something to compare, so both drop out for a lone row — whoever renders it names
 * and times it on its own.
 */
export function OptJobMultiPhaseProgressMatrix({
  rows,
  tone = 'light',
}: OptJobMultiPhaseProgressMatrixProps): ReactNode {
  const { t } = useTranslation()
  const c = TONE_CLASSES[tone]

  // Both flanking columns only say something when there is more than one row: a lone
  // item is already named in the row's title, and its total is the job's own.
  const hasSeveralRows = rows.length > 1
  // Two rows are read as a pair; stripes start paying for their contrast above that.
  const showStripes = rows.length > 2

  const templateColumns = [
    hasSeveralRows ? 'minmax(3rem, 7rem)' : null,
    `repeat(${STEPS.length}, minmax(3rem, 1fr))`,
    hasSeveralRows ? 'auto' : null,
  ]
    .filter(Boolean)
    .join(' ')

  // Without those columns the phase cells are the row's own edges.
  const phaseCellEdges = (stepIndex: number, lastStepIndex: number): string => {
    if (hasSeveralRows) return ''

    return [stepIndex === 0 ? 'rounded-l-md' : null, stepIndex === lastStepIndex ? 'rounded-r-md' : null]
      .filter(Boolean)
      .join(' ')
  }

  return (
    <div className="text-xs">
      <div className="grid gap-y-2 items-stretch" style={{ gridTemplateColumns: templateColumns }}>
        {/* Header row — empty cell aligns with the label column */}
        {hasSeveralRows && <div />}
        {STEPS.map((step) => (
          <div key={step.id} className={`text-[0.65rem] uppercase tracking-wide ${c.header} text-center px-1.5`}>
            {t(step.labelKey)}
          </div>
        ))}
        {hasSeveralRows && (
          <div className={`text-[0.65rem] uppercase tracking-wide ${c.header} text-right pl-1.5`}>{t('total')}</div>
        )}

        {/* Data rows */}
        {rows.map((row, index) => {
          const stripeBg = showStripes && index % 2 === 1 ? 'bg-white/5' : ''
          return (
            <div key={row.id} className="contents">
              {hasSeveralRows && (
                <div className={`${c.label} leading-tight flex items-center py-1 pl-1 rounded-l-md ${stripeBg}`}>
                  {row.label != null ? withWrapPoints(row.label) : NO_SUBTYPE_LABEL}
                </div>
              )}
              {row.steps.map((step, stepIndex) => {
                const cell = (
                  <div className={cellClassName(step.state, c.pendingCell)}>
                    {step.durationMs != null && (
                      <span className="text-white text-[9px] font-medium tabular-nums leading-none">
                        {formatElapsed(step.durationMs)}
                      </span>
                    )}
                  </div>
                )
                // Every phase from the one that broke onwards shares its outcome, so each answers
                // the same question: why this item has no result.
                const failureExplanationKey =
                  step.state === 'failed' || step.state === 'unsolvable' ? row.failureExplanationKey : null

                return (
                  <div
                    key={step.id}
                    className={`px-1.5 py-1 flex items-center ${stripeBg} ${phaseCellEdges(stepIndex, row.steps.length - 1)}`}
                  >
                    {failureExplanationKey != null ? (
                      <Tooltip arrow title={t(failureExplanationKey)}>
                        {cell}
                      </Tooltip>
                    ) : (
                      cell
                    )}
                  </div>
                )
              })}
              {hasSeveralRows && (
                <div
                  className={`flex items-center justify-end pl-1.5 pr-1 font-semibold tabular-nums ${c.label} ${stripeBg} rounded-r-md`}
                >
                  {formatElapsed(row.elapsedMs)}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
